/**
 * Pre-wrap trust evaluation for conversation messages, reactions, and calls.
 *
 * - Never wrap to a device whose static-key attestation does not verify.
 * - Pin signing key + attested device material on first successful fetch (TOFU).
 * - New or changed devices, signing-key changes, and new conversation members
 *   are reported in `blocked` so the UI can warn (or pause send/call when the
 *   user opts in).
 */

import type { CryptoProfile } from '@adieuu/crypto';
import type { PublicDevice } from '@adieuu/shared';
import { deviceStaticAttestationHolds } from './deviceAttestation';
import {
  addConversationMemberPin,
  getConversationMemberPin,
  getPeerIdentityPin,
  setConversationMemberPin,
  setPeerIdentityPin,
  type PinnedPeerDevice,
  type PinnedPeerIdentity,
} from './peerKeyPinStorage';

export type PeerTrustBlockReason =
  | 'device_change'
  | 'signing_key_change'
  | 'new_member'
  | 'no_attested_device';

export interface PeerTrustBlock {
  identityId: string;
  reason: PeerTrustBlockReason;
}

export interface TrustableRecipient {
  identityId: string;
  signingPublicKey: string;
  pqSigningPublicKey?: string;
  preferredCryptoProfile: CryptoProfile;
  devices: PublicDevice[];
  preKeys?: unknown;
}

export class PeerTrustBlockedError extends Error {
  readonly code = 'PEER_TRUST_BLOCKED';
  readonly identityIds: string[];
  readonly blocked: PeerTrustBlock[];

  constructor(blocked: PeerTrustBlock[]) {
    const ids = [...new Set(blocked.map((b) => b.identityId))];
    super(`Peer trust blocked for ${ids.join(', ')}`);
    this.name = 'PeerTrustBlockedError';
    this.identityIds = ids;
    this.blocked = blocked;
  }
}

export function isPeerTrustBlockedError(err: unknown): err is PeerTrustBlockedError {
  return err instanceof PeerTrustBlockedError ||
    (typeof err === 'object' &&
      err != null &&
      (err as { code?: string }).code === 'PEER_TRUST_BLOCKED');
}

export function attestedDevicesOf(
  recipient: TrustableRecipient,
): PublicDevice[] {
  const profile = recipient.preferredCryptoProfile ?? 'default';
  return recipient.devices.filter((device) =>
    deviceStaticAttestationHolds(
      recipient.signingPublicKey,
      device,
      profile,
      recipient.pqSigningPublicKey,
    ),
  );
}

function snapshotDevice(device: PublicDevice): PinnedPeerDevice {
  return {
    deviceId: device.deviceId,
    ecdhPublicKey: device.ecdhPublicKey,
    kemPublicKey: device.kemPublicKey ?? '',
  };
}

function deviceSetsEqual(a: PinnedPeerDevice[], b: PinnedPeerDevice[]): boolean {
  if (a.length !== b.length) return false;
  const left = [...a].sort((x, y) => x.deviceId.localeCompare(y.deviceId));
  const right = [...b].sort((x, y) => x.deviceId.localeCompare(y.deviceId));
  return left.every(
    (d, i) =>
      d.deviceId === right[i]!.deviceId &&
      d.ecdhPublicKey === right[i]!.ecdhPublicKey &&
      d.kemPublicKey === right[i]!.kemPublicKey,
  );
}

export function pinRecordFromRecipient(recipient: TrustableRecipient): PinnedPeerIdentity | null {
  const devices = attestedDevicesOf(recipient);
  if (devices.length === 0) return null;
  return {
    identityId: recipient.identityId,
    signingPublicKey: recipient.signingPublicKey,
    devices: devices.map(snapshotDevice),
    pinnedAt: new Date().toISOString(),
  };
}

export async function acceptPeerIdentityPin(
  conversationId: string,
  recipient: TrustableRecipient,
): Promise<boolean> {
  const record = pinRecordFromRecipient(recipient);
  if (!record) return false;
  await setPeerIdentityPin(record);
  await addConversationMemberPin(conversationId, recipient.identityId);
  return true;
}

export async function peerIdentityNeedsAccept(
  conversationId: string,
  recipient: TrustableRecipient,
): Promise<boolean> {
  const memberPin = await getConversationMemberPin(conversationId);
  if (memberPin && !memberPin.identityIds.includes(recipient.identityId)) {
    return true;
  }
  const pin = await getPeerIdentityPin(recipient.identityId);
  const next = pinRecordFromRecipient(recipient);
  if (!next) return true;
  if (!pin) return true;
  if (pin.signingPublicKey !== next.signingPublicKey) return true;
  return !deviceSetsEqual(pin.devices, next.devices);
}

export interface PeerTrustEvaluation {
  recipients: TrustableRecipient[];
  blocked: PeerTrustBlock[];
}

function withAttestedDevicesOnly(recipient: TrustableRecipient): TrustableRecipient {
  const attested = attestedDevicesOf(recipient);
  if (attested.length === recipient.devices.length) return recipient;
  return { ...recipient, devices: attested };
}

/**
 * Filter fetched recipient keys to attested devices and record TOFU pins on
 * first successful fetch.
 *
 * `blocked` is non-empty when the user should review new or changed devices.
 * Pause-mode callers must not wrap while `blocked` is non-empty. Warn-mode
 * callers pass `includeBlockedInWrapable` so attested devices of blocked
 * identities remain wrap targets.
 */
export async function evaluatePeerTrust(input: {
  conversationId: string;
  fetched: TrustableRecipient[];
  requestedParticipantIds: string[];
  includeBlockedInWrapable?: boolean;
}): Promise<PeerTrustEvaluation> {
  const includeBlocked = input.includeBlockedInWrapable === true;
  const blocked: PeerTrustBlock[] = [];
  const wrapable: TrustableRecipient[] = [];

  const pushWrapable = (recipient: TrustableRecipient, isBlocked: boolean) => {
    if (!isBlocked || includeBlocked) {
      wrapable.push(recipient);
    }
  };

  const memberPin = await getConversationMemberPin(input.conversationId);
  if (!memberPin && input.requestedParticipantIds.length > 0) {
    await setConversationMemberPin({
      conversationId: input.conversationId,
      identityIds: [...new Set(input.requestedParticipantIds)].sort(),
      pinnedAt: new Date().toISOString(),
    });
  }
  const allowedMembers = new Set(
    (memberPin ?? {
      identityIds: [...new Set(input.requestedParticipantIds)],
    }).identityIds,
  );

  for (const pid of input.requestedParticipantIds) {
    if (!allowedMembers.has(pid)) {
      blocked.push({ identityId: pid, reason: 'new_member' });
    }
  }

  const fetchedById = new Map(input.fetched.map((r) => [r.identityId, r]));

  for (const recipient of input.fetched) {
    if (!allowedMembers.has(recipient.identityId)) {
      if (!blocked.some((b) => b.identityId === recipient.identityId)) {
        blocked.push({ identityId: recipient.identityId, reason: 'new_member' });
      }
      const attested = withAttestedDevicesOnly(recipient);
      if (attested.devices.length > 0) pushWrapable(attested, true);
      continue;
    }

    const attested = withAttestedDevicesOnly(recipient);
    if (attested.devices.length === 0) {
      blocked.push({ identityId: recipient.identityId, reason: 'no_attested_device' });
      continue;
    }

    const existing = await getPeerIdentityPin(recipient.identityId);
    if (!existing) {
      const record = pinRecordFromRecipient(attested);
      if (record) await setPeerIdentityPin(record);
      wrapable.push(attested);
      continue;
    }

    if (existing.signingPublicKey !== attested.signingPublicKey) {
      blocked.push({ identityId: recipient.identityId, reason: 'signing_key_change' });
      pushWrapable(attested, true);
      continue;
    }

    const nextDevices = attested.devices.map(snapshotDevice);
    if (!deviceSetsEqual(existing.devices, nextDevices)) {
      blocked.push({ identityId: recipient.identityId, reason: 'device_change' });
      pushWrapable(attested, true);
      continue;
    }

    wrapable.push(attested);
  }

  for (const pid of input.requestedParticipantIds) {
    if (!allowedMembers.has(pid)) continue;
    if (fetchedById.has(pid)) continue;
    // Participant listed locally but keys were not fetched this round: do not
    // treat as a new member (already pinned) and do not invent a wrap target.
  }

  return { recipients: wrapable, blocked };
}

export function throwIfPeerTrustBlocked(evaluation: PeerTrustEvaluation): void {
  if (evaluation.blocked.length > 0) {
    throw new PeerTrustBlockedError(evaluation.blocked);
  }
}

/**
 * Stable id for a set of trust blocks plus the current device ids of those
 * identities, so a dismissed warning reappears when the device set changes.
 */
export function peerTrustBlocksFingerprint(
  blocks: PeerTrustBlock[],
  deviceIdsByIdentity: Record<string, readonly string[] | undefined>,
): string {
  return [...blocks]
    .map((block) => {
      const devices = [...(deviceIdsByIdentity[block.identityId] ?? [])].sort().join(',');
      return `${block.identityId}:${block.reason}:${devices}`;
    })
    .sort()
    .join('|');
}
